import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, api } from '@/api/client'
import { getLocale, type AppLocale } from '@/config/locale'

const nullableString = z.string().nullable().optional()
const nullableNumber = z.number().nullable().optional()
const booleanish = z
  .union([z.boolean(), z.number(), z.string()])
  .transform((value) => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value !== 0
    return ['1', 'true'].includes(value.trim().toLowerCase())
  })

export type MembershipStatus = 'accepted' | 'pending' | 'rejected' | null
export type MembershipRole = 'owner' | 'admin' | 'member' | null
export type StageType = 'group' | 'league' | 'knockout'
export type PouleUxStageType = 'group' | 'knockout' | 'league_stage' | 'classic_league'
export type MatchListStatus = 'future' | 'past'
export type MatchStatus = 'scheduled' | 'live' | 'ht' | 'ft' | 'cancelled' | 'postponed'
export type PredictStatus = 'open' | 'closed' | 'mixed'

export interface PouleInfoResponse {
  poule: {
    id: number
    name: string
    description?: string | null | undefined
    hero_url?: string | null | undefined
    profile_url?: string | null | undefined
    thumbnail_url?: string | null | undefined
    season_id?: number | null | undefined
    poule_type?: string | null | undefined
    audience_type?: string | null | undefined
    activation_status?: 'pending' | 'active' | null | undefined
    license_required?: boolean | undefined
    license_product_key?: string | null | undefined
    supports_teams?: boolean | undefined
    requires_approval?: boolean | undefined
    visibility?: string | null | undefined
    closed?: boolean | undefined
    prizes_configured?: boolean | undefined
    has_prizes?: boolean | undefined
    prizes_count?: number | null | undefined
  }
  membership?: {
    status?: MembershipStatus | undefined
    role?: MembershipRole | undefined
  } | null | undefined
  member_count?: number | undefined
  admins?: Array<Record<string, unknown>> | undefined
  owner?: Record<string, unknown> | null | undefined
}

const rawPublicPouleSearchItemSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    description: nullableString,
    thumbnail_url: nullableString,
    hero_url: nullableString,
    requires_approval: booleanish.optional(),
    members_count: nullableNumber,
    is_member: booleanish.optional(),
    classification: nullableString,
    audience_type: nullableString,
    activation_status: z.enum(['pending', 'active']).nullable().optional(),
    license_required: z.boolean().optional(),
    license_product_key: nullableString,
    supports_teams: booleanish.optional(),
  })
  .passthrough()

const rawPublicPouleSearchResponseSchema = z
  .object({
    data: z.array(rawPublicPouleSearchItemSchema).optional().default([]),
    current_page: nullableNumber,
    last_page: nullableNumber,
    total: nullableNumber,
  })
  .passthrough()

export interface Stage {
  id: number
  external_id?: number | null | undefined
  name: string
  type: StageType
  order?: number | null | undefined
  is_current: boolean
}

export interface PredictionStageContextDto {
  mode?: string | null | undefined
  active_stage_name?: string | null | undefined
  stage_lock_starts_at_utc?: string | null | undefined
  stage_lock_starts_at_nl?: string | null | undefined
  seconds_until_stage_lock?: number | null | undefined
  predictions_open: boolean
}

export interface Team {
  id: number
  name: string
  short_name?: string | null | undefined
  logo_url: string
  is_placeholder?: boolean | undefined
}

export interface UserPrediction {
  home_pred?: number | null | undefined
  away_pred?: number | null | undefined
  points_awarded?: number | null | undefined
  virtual_points?: number | null | undefined
  poule_id?: number | null | undefined
}

export interface PredictionLockContextDto {
  current_stage_name?: string | null | undefined
  match_stage_name?: string | null | undefined
}

export interface MatchEvent {
  id: number
  minute: number
  extra?: number | null | undefined
  team_side?: string | null | undefined
  player_id?: number | null | undefined
  player?: string | null | undefined
  info?: string | null | undefined
  type?: 'goal' | 'yellow' | 'red' | 'sub' | 'other' | string | null | undefined
  team_badge?: string | null | undefined
  addition?: string | null | undefined
}

export interface LineupPlayer {
  id?: number | null | undefined
  name: string
  position?: string | null | undefined
  jersey?: number | null | undefined
  is_starting: boolean
  image?: string | null | undefined
}

export interface FootballMatch {
  id: number
  season_id?: number | null | undefined
  league_id?: number | null | undefined
  status: MatchStatus
  predict_status: PredictStatus
  is_predictable: boolean
  prediction_locked_by_stage_release?: boolean | null | undefined
  prediction_locked_reason?: string | null | undefined
  prediction_lock_context?: PredictionLockContextDto | null | undefined
  round?: number | null | undefined
  joker?: boolean | undefined
  joker_multiplier?: number | null | undefined
  kickoff_utc: string
  kickoff_nl?: string | null | undefined
  kickoff_time_tbd?: boolean | undefined
  minute?: number | null | undefined
  extra_minute?: number | null | undefined
  home_team: Team
  away_team: Team
  score?: {
    home?: number | null | undefined
    away?: number | null | undefined
  } | null | undefined
  home_form?: string[] | undefined
  away_form?: string[] | undefined
  stage_id?: number | null | undefined
  stage_name?: string | null | undefined
  stage_type?: string | null | undefined
  external_group_id?: number | null | undefined
  group_id?: number | null | undefined
  poule?: {
    id?: number | null | undefined
    name?: string | null | undefined
    poule_type?: string | null | undefined
    scope_type?: string | null | undefined
  } | null | undefined
  poule_match_id?: number | null | undefined
  chat_active?: boolean | undefined
  chat_writable?: boolean | undefined
  user_prediction?: UserPrediction | null | undefined
  events?: MatchEvent[] | undefined
  homeLineup?: LineupPlayer[] | undefined
  homeBench?: LineupPlayer[] | undefined
  awayLineup?: LineupPlayer[] | undefined
  awayBench?: LineupPlayer[] | undefined
}

export interface OtherPredictionItem {
  user_name: string
  home_pred: number
  away_pred: number
  points_awarded?: number | null | undefined
  provisional_points?: number | null | undefined
  user?: {
    profile_image?: string | null | undefined
  } | null | undefined
}

export interface OtherPredictionsResponse {
  predictions: OtherPredictionItem[]
}

export interface MatchPredictionStatsResponse {
  match: {
    id: number
    status: string
    kickoff_utc?: string | null | undefined
    home_team: Team
    away_team: Team
  }
  summary: {
    accepted_members_count: number
    submitted_predictions_count: number
    missing_predictions_count: number
    coverage_percentage: number
  }
  most_used_prediction?: {
    home_pred?: number | null | undefined
    away_pred?: number | null | undefined
    label: string
    count: number
    percentage: number
  } | null | undefined
  outcome_distribution?: {
    total: number
    home_team: Team
    away_team: Team
    items: Array<{
      key: 'home_win' | 'draw' | 'away_win' | string
      count: number
      percentage: number
    }>
  } | null | undefined
  top_scorelines: Array<{
    home_pred?: number | null | undefined
    away_pred?: number | null | undefined
    label: string
    count: number
    percentage: number
  }>
}

export interface PaginatedMatchesResponse {
  data: FootballMatch[]
  currentPage: number
  lastPage: number
  total: number
  predictionStageContext?: PredictionStageContextDto | null | undefined
}

export interface PouleDashboardBootstrapResponse {
  meta?: {
    schema_version?: number | null | undefined
    generated_at?: string | null | undefined
    includes?: string[] | undefined
    activities_limit?: number | null | undefined
    badges_limit?: number | null | undefined
  } | null | undefined
  poule?: {
    id: number
    season_id?: number | null | undefined
    season_name?: string | null | undefined
    type?: string | null | undefined
    closed?: boolean | undefined
    closed_at?: string | null | undefined
  } | null | undefined
  dashboard?: {
    has_any_predictions?: boolean | undefined
    round?: {
      round?: {
        id?: number | undefined
        sequence?: number | null | undefined
        name?: string | null | undefined
        type?: string | null | undefined
      } | undefined
      points?: number | null | undefined
      matches?: number | null | undefined
    } | null | undefined
    standing?: {
      rank?: number | null | undefined
      total_users?: number | null | undefined
    } | null | undefined
    form?: Record<string, unknown> | null | undefined
    accuracy?: Record<string, unknown> | null | undefined
    badges?: Record<string, unknown> | null | undefined
  } | null | undefined
  round_winner?: Record<string, unknown> | null | undefined
  quiz_stats?: Record<string, unknown> | null | undefined
  quiz_availability?: Record<string, unknown> | null | undefined
  bonus_availability?: Record<string, unknown> | null | undefined
  prediction_availability?: Record<string, unknown> | null | undefined
  advanced_fantasy_availability?: Record<string, unknown> | null | undefined
  recent_badges_compact?: Array<Record<string, unknown>> | undefined
  activities?: { items?: Array<Record<string, unknown>> | undefined; limit?: number | null | undefined } | null | undefined
  poule_rule?: Record<string, unknown> | null | undefined
  closure_summary?: Record<string, unknown> | null | undefined
}

export interface PouleAppRound {
  id: number
  sequence?: number | null | undefined
  name?: string | null | undefined
  is_current?: boolean | undefined
  status?: string | null | undefined
  has_scores?: boolean | undefined
  type?: string | null | undefined
  starts_at?: string | null | undefined
  ends_at?: string | null | undefined
}

export interface PouleRoundRankingEntry {
  position: number
  points: number
  is_me: boolean
  user_id?: number | null | undefined
  user_name: string
  profile_image?: string | null | undefined
}

export interface SavePoulePredictionInput {
  matchId: number
  pouleId: number
  homePred: number
  awayPred: number
}

export interface SavedPoulePrediction {
  home_pred?: number | null | undefined
  away_pred?: number | null | undefined
  poule_id?: number | null | undefined
  points_awarded?: number | null | undefined
  virtual_points?: number | null | undefined
}

export interface BadgeDefinition {
  id?: number | undefined
  key?: string | null | undefined
  name: string
  icon_url?: string | null | undefined
  description?: string | null | undefined
  category?: string | null | undefined
  points_reward?: number | null | undefined
  difficulty?: number | null | undefined
}

export interface BadgeTier {
  key?: string | null | undefined
  label?: string | null | undefined
}

export interface ClubSummary {
  id?: number | null | undefined
  name?: string | null | undefined
  logo?: string | null | undefined
}

export interface UserBadgeInstance {
  id: number
  earned_at: string
  reason?: string | null | undefined
  meta?: Record<string, unknown> | null | undefined
  badge?: Partial<BadgeDefinition> | null | undefined
  tier?: BadgeTier | null | undefined
  club?: ClubSummary | null | undefined
  poule_id?: number | null | undefined
  poule?: {
    id?: number | null | undefined
    name?: string | null | undefined
    thumbnail_url?: string | null | undefined
    competition?: {
      logo_url?: string | null | undefined
    } | null | undefined
  } | null | undefined
  prediction?: UserPrediction | null | undefined
  match?: FootballMatch | null | undefined
  matches?: FootballMatch[] | undefined
}

export interface BadgeUser {
  id: number
  name: string
  display_name?: string | null | undefined
  profile_image?: string | null | undefined
}

export interface BadgeOverviewItem {
  id: number
  badge: BadgeDefinition
  tier?: BadgeTier | null | undefined
  earned: boolean
  earned_count: number
  last_earned_at?: string | null | undefined
  club?: ClubSummary | null | undefined
  earned_instances: UserBadgeInstance[]
  earned_by_users_count: number
  earned_by_users: BadgeUser[]
}

export interface AllBadgesResponse {
  data: BadgeOverviewItem[]
}

export type BadgeCatalogItem = BadgeDefinition
export type BadgeClub = ClubSummary
export type BadgeUserSummary = BadgeUser

export interface GetUserBadgesOptions {
  compact?: boolean
  lang?: AppLocale
}

const membershipStatusSchema = z.enum(['accepted', 'pending', 'rejected']).nullable()
const membershipRoleSchema = z.enum(['owner', 'admin', 'member']).nullable()
const stageTypeSchema = z.enum(['group', 'league', 'knockout'])

const pouleMembershipSchema = z
  .object({
    status: membershipStatusSchema.optional(),
    role: membershipRoleSchema.optional(),
  })
  .passthrough()

const pouleDetailSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    description: nullableString,
    hero_url: nullableString,
    profile_url: nullableString,
    thumbnail_url: nullableString,
    season_id: nullableNumber,
    poule_type: nullableString,
    audience_type: nullableString,
    activation_status: z.enum(['pending', 'active']).nullable().optional(),
    license_required: z.boolean().optional(),
    requires_approval: booleanish.optional(),
    visibility: nullableString,
    closed: booleanish.optional(),
    supports_teams: z.boolean().optional(),
    prizes_configured: z.boolean().optional(),
    has_prizes: z.boolean().optional(),
    prizes_count: nullableNumber,
  })
  .passthrough()

const pouleInfoResponseSchema = z
  .object({
    poule: pouleDetailSchema,
    membership: pouleMembershipSchema.nullable().optional(),
    member_count: z.number().optional(),
    admins: z.array(z.record(z.string(), z.unknown())).optional().default([]),
    owner: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .passthrough()

const stageSchema = z
  .object({
    id: z.number(),
    external_id: nullableNumber,
    name: z.string(),
    type: stageTypeSchema,
    order: nullableNumber,
    is_current: z.boolean(),
  })
  .passthrough()

const stagesEnvelopeSchema = z.union([
  z.array(stageSchema),
  z
    .object({
      stages: z.array(stageSchema).optional().default([]),
    })
    .passthrough(),
])

const rawLineupPlayerSchema = z
  .object({
    id: nullableNumber,
    name: nullableString,
    player_name: nullableString,
    display_name: nullableString,
    position: nullableString,
    jersey: nullableNumber,
    shirt_number: nullableNumber,
    squad_number: nullableNumber,
    is_starting: z.boolean().optional(),
    image: nullableString,
    photo_url: nullableString,
    image_url: nullableString,
  })
  .passthrough()

const rawMatchEventSchema = z
  .object({
    id: z.number(),
    minute: z.number().optional().default(0),
    extra: nullableNumber,
    team_side: nullableString,
    player_id: nullableNumber,
    player: nullableString,
    info: nullableString,
    type: nullableString,
    team_badge: nullableString,
    addition: nullableString,
  })
  .passthrough()

const rawTeamSchema = z
  .object({
    id: z.number().nullable().optional(),
    name: nullableString,
    display_name: nullableString,
    short_name: nullableString,
    logo_url: nullableString,
    is_placeholder: z.boolean().optional(),
    lineup: z.array(rawLineupPlayerSchema).optional().default([]),
    bench: z.array(rawLineupPlayerSchema).optional().default([]),
  })
  .passthrough()

const rawPredictionSchema = z
  .object({
    home_pred: nullableNumber,
    away_pred: nullableNumber,
    points_awarded: nullableNumber,
    virtual_points: nullableNumber,
    poule_id: nullableNumber,
  })
  .passthrough()

const rawFootballMatchSchema = z
  .object({
    id: z.number(),
    season_id: nullableNumber,
    league_id: nullableNumber,
    status: nullableString,
    predict_status: nullableString,
    is_predictable: z.boolean().optional(),
    prediction_locked_by_stage_release: z.boolean().nullable().optional(),
    prediction_locked_reason: nullableString,
    prediction_lock_context: z
      .object({
        current_stage_name: nullableString,
        match_stage_name: nullableString,
      })
      .passthrough()
      .nullable()
      .optional(),
    round: z.union([z.number(), z.string(), z.null()]).optional(),
    joker: z.boolean().optional(),
    joker_multiplier: nullableNumber,
    kickoff_utc: nullableString,
    kickoff_nl: nullableString,
    kickoff_time_tbd: z.boolean().optional(),
    minute: nullableNumber,
    extra_minute: nullableNumber,
    home_team: rawTeamSchema.optional(),
    away_team: rawTeamSchema.optional(),
    score: z
      .object({
        home: nullableNumber,
        away: nullableNumber,
      })
      .passthrough()
      .nullable()
      .optional(),
    home_form: z.array(z.string()).optional().default([]),
    away_form: z.array(z.string()).optional().default([]),
    stage_id: nullableNumber,
    stage_name: nullableString,
    stage_type: nullableString,
    external_group_id: nullableNumber,
    group_id: nullableNumber,
    poule: z
      .object({
        id: nullableNumber,
        name: nullableString,
        poule_type: nullableString,
        scope_type: nullableString,
      })
      .passthrough()
      .nullable()
      .optional(),
    poule_match_id: nullableNumber,
    chat_active: z.boolean().optional(),
    chat_writable: z.boolean().optional(),
    is_cancelled: z.boolean().optional(),
    user_prediction: rawPredictionSchema.nullable().optional(),
    events: z.array(rawMatchEventSchema).optional().default([]),
  })
  .passthrough()

const rawPredictionStageContextSchema = z
  .object({
    mode: nullableString,
    active_stage_name: nullableString,
    stage_lock_starts_at_utc: nullableString,
    stage_lock_starts_at_nl: nullableString,
    seconds_until_stage_lock: nullableNumber,
    predictions_open: z.boolean().optional().default(false),
  })
  .passthrough()

const rawPaginatedMatchesSchema = z
  .object({
    data: z.array(rawFootballMatchSchema).optional().default([]),
    current_page: z.number().optional(),
    last_page: z.number().optional(),
    total: z.number().optional(),
    meta: z
      .object({
        current_page: z.number().optional(),
        last_page: z.number().optional(),
        total: z.number().optional(),
      })
      .passthrough()
      .optional(),
    prediction_stage_context: rawPredictionStageContextSchema.nullable().optional(),
  })
  .passthrough()

const rawDashboardSchema = z
  .object({
    has_any_predictions: z.boolean().optional(),
    round: z
      .object({
        round: z
          .object({
            id: z.number().optional(),
            sequence: nullableNumber,
            name: nullableString,
            type: nullableString,
          })
          .passthrough()
          .optional(),
        points: nullableNumber,
        matches: nullableNumber,
      })
      .passthrough()
      .nullable()
      .optional(),
    standing: z
      .object({
        rank: nullableNumber,
        total_users: nullableNumber,
      })
      .passthrough()
      .nullable()
      .optional(),
    form: z.record(z.string(), z.unknown()).nullable().optional(),
    accuracy: z.record(z.string(), z.unknown()).nullable().optional(),
    badges: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .passthrough()

const rawAppRoundSchema = z
  .object({
    id: z.number(),
    sequence: nullableNumber,
    name: nullableString,
    is_current: z.boolean().optional(),
    status: nullableString,
    has_scores: z.boolean().optional(),
    type: nullableString,
    starts_at: nullableString,
    ends_at: nullableString,
  })
  .passthrough()

const rawAppRoundsResponseSchema = z
  .object({
    rounds: z.array(rawAppRoundSchema).optional().default([]),
  })
  .passthrough()

const rawRoundRankingUserSchema = z
  .object({
    id: nullableNumber,
    display_name: nullableString,
    first_name: nullableString,
    last_name: nullableString,
    profile_image: nullableString,
  })
  .passthrough()

const rawRoundRankingEntrySchema = z
  .object({
    position: nullableNumber,
    points: nullableNumber,
    is_me: z.boolean().optional(),
    poule_user: z
      .object({
        user: rawRoundRankingUserSchema.nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough()

const rawRoundRankingResponseSchema = z
  .object({
    ranking: z.array(rawRoundRankingEntrySchema).optional().default([]),
  })
  .passthrough()

const rawPouleActivityFeedSchema = z
  .object({
    items: z.array(z.record(z.string(), z.unknown())).optional().default([]),
    limit: nullableNumber,
  })
  .passthrough()

const rawPouleDashboardBootstrapResponseSchema = z
  .object({
    meta: z
      .object({
        schema_version: nullableNumber,
        generated_at: nullableString,
        includes: z.array(z.string()).optional().default([]),
        activities_limit: nullableNumber,
        badges_limit: nullableNumber,
      })
      .passthrough()
      .nullable()
      .optional(),
    poule: z
      .object({
        id: z.number(),
        season_id: nullableNumber,
        season_name: nullableString,
        type: nullableString,
        closed: z.boolean().optional(),
        closed_at: nullableString,
      })
      .passthrough()
      .nullable()
      .optional(),
    dashboard: rawDashboardSchema.nullable().optional(),
    round_winner: z.record(z.string(), z.unknown()).nullable().optional(),
    quiz_stats: z.record(z.string(), z.unknown()).nullable().optional(),
    quiz_availability: z.record(z.string(), z.unknown()).nullable().optional(),
    bonus_availability: z.record(z.string(), z.unknown()).nullable().optional(),
    prediction_availability: z.record(z.string(), z.unknown()).nullable().optional(),
    advanced_fantasy_availability: z.record(z.string(), z.unknown()).nullable().optional(),
    recent_badges_compact: z.array(z.record(z.string(), z.unknown())).optional().default([]),
    activities: rawPouleActivityFeedSchema.nullable().optional(),
    poule_rule: z.record(z.string(), z.unknown()).nullable().optional(),
    closure_summary: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .passthrough()

const rawLiveMatchesResponseSchema = z
  .object({
    data: z.array(rawFootballMatchSchema).optional(),
    matches: z.array(rawFootballMatchSchema).optional(),
    items: z.array(rawFootballMatchSchema).optional(),
  })
  .passthrough()

const savedPredictionSchema = z
  .object({
    home_pred: nullableNumber,
    away_pred: nullableNumber,
    poule_id: nullableNumber,
    points_awarded: nullableNumber,
    virtual_points: nullableNumber,
  })
  .passthrough()

const rawBadgeCatalogItemSchema = z
  .object({
    id: z.number().optional(),
    key: nullableString,
    name: z.string(),
    icon_url: nullableString,
    description: nullableString,
    category: nullableString,
    points_reward: nullableNumber,
    difficulty: nullableNumber,
  })
  .passthrough()

const rawBadgeTierSchema = z
  .object({
    key: nullableString,
    label: nullableString,
  })
  .passthrough()

const rawBadgeClubSchema = z
  .object({
    id: nullableNumber,
    name: nullableString,
    logo: nullableString,
  })
  .passthrough()

const rawBadgeUserSummarySchema = z
  .object({
    id: z.number(),
    name: z.string(),
    display_name: nullableString,
    profile_image: nullableString,
  })
  .passthrough()

const rawBadgePouleSchema = z
  .object({
    id: nullableNumber,
    name: nullableString,
    thumbnail_url: nullableString,
    competition: z
      .object({
        logo_url: nullableString,
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough()

const rawBadgeUserSchema = z
  .object({
    id: z.number(),
    earned_at: nullableString,
    reason: nullableString,
    meta: z.record(z.string(), z.unknown()).nullable().optional(),
    badge: rawBadgeCatalogItemSchema.partial().nullable().optional(),
    tier: rawBadgeTierSchema.nullable().optional(),
    club: rawBadgeClubSchema.nullable().optional(),
    poule_id: nullableNumber,
    poule: rawBadgePouleSchema.nullable().optional(),
    prediction: rawPredictionSchema.nullable().optional(),
    match: rawFootballMatchSchema.nullable().optional(),
    matches: z.array(rawFootballMatchSchema).optional().default([]),
  })
  .passthrough()

const rawBadgeOverviewItemSchema = z
  .object({
    id: z.number(),
    badge: rawBadgeCatalogItemSchema,
    tier: rawBadgeTierSchema.nullable().optional(),
    earned: z.boolean().optional().default(false),
    earned_count: z.number().optional().default(0),
    last_earned_at: nullableString,
    club: rawBadgeClubSchema.nullable().optional(),
    earned_instances: z.array(rawBadgeUserSchema).optional().default([]),
    earned_by_users_count: z.number().optional().default(0),
    earned_by_users: z.array(rawBadgeUserSummarySchema).optional().default([]),
  })
  .passthrough()

const rawAllBadgesResponseSchema = z
  .object({
    data: z.array(rawBadgeOverviewItemSchema).optional().default([]),
  })
  .passthrough()

const rawOtherPredictionItemSchema = z
  .object({
    user_name: z.string(),
    home_pred: z.number(),
    away_pred: z.number(),
    points_awarded: nullableNumber,
    provisional_points: nullableNumber,
    user: z
      .object({
        profile_image: nullableString,
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough()

const rawOtherPredictionsResponseSchema = z
  .object({
    predictions: z.array(rawOtherPredictionItemSchema).optional().default([]),
  })
  .passthrough()

const rawPredictionStatsResponseSchema = z
  .object({
    match: z
      .object({
        id: z.number(),
        status: z.string(),
        kickoff_utc: nullableString,
        home_team: rawTeamSchema,
        away_team: rawTeamSchema,
      })
      .passthrough(),
    summary: z
      .object({
        accepted_members_count: z.number(),
        submitted_predictions_count: z.number(),
        missing_predictions_count: z.number(),
        coverage_percentage: z.number(),
      })
      .passthrough(),
    most_used_prediction: z
      .object({
        home_pred: nullableNumber,
        away_pred: nullableNumber,
        label: z.string(),
        count: z.number(),
        percentage: z.number(),
      })
      .passthrough()
      .nullable()
      .optional(),
    outcome_distribution: z
      .object({
        total: z.number(),
        home_team: rawTeamSchema,
        away_team: rawTeamSchema,
        items: z
          .array(
            z
              .object({
                key: z.string(),
                count: z.number(),
                percentage: z.number(),
              })
              .passthrough(),
          )
          .optional()
          .default([]),
      })
      .passthrough()
      .nullable()
      .optional(),
    top_scorelines: z
      .array(
        z
          .object({
            home_pred: nullableNumber,
            away_pred: nullableNumber,
            label: z.string(),
            count: z.number(),
            percentage: z.number(),
          })
          .passthrough(),
      )
      .optional()
      .default([]),
  })
  .passthrough()

export type PouleDetail = PouleInfoResponse['poule']
export type PouleMembership = NonNullable<PouleInfoResponse['membership']>
export type PublicPouleSearchItem = z.infer<typeof rawPublicPouleSearchItemSchema>
export type PublicPouleSearchResponse = z.infer<typeof rawPublicPouleSearchResponseSchema>
export type PouleDashboard = NonNullable<PouleDashboardBootstrapResponse['dashboard']>
export type PouleRoundWinner = NonNullable<PouleDashboardBootstrapResponse['round_winner']>
export type QuizAvailability = NonNullable<
  PouleDashboardBootstrapResponse['quiz_availability']
>
export type BonusAvailability = NonNullable<
  PouleDashboardBootstrapResponse['bonus_availability']
>
export type PredictionAvailability = NonNullable<
  PouleDashboardBootstrapResponse['prediction_availability']
>
export type PouleActivityFeed = NonNullable<PouleDashboardBootstrapResponse['activities']>

export interface PouleCreateCountry {
  id: number
  name: string
  image_path?: string | null | undefined
  flag_url?: string | null | undefined
}

export interface PouleCreateLeague {
  id: number
  name: string
  image_path?: string | null | undefined
}

export interface PouleCreateTeam {
  id: number
  name: string
  logo_url?: string | null | undefined
  is_placeholder?: boolean | undefined
}

export interface PouleCreateTournament {
  id: number
  name: string
  image_path?: string | null | undefined
  seasons?: Array<{ id: number; name: string }> | undefined
}

export interface PouleCreateResponse {
  success?: boolean | undefined
  poule: {
    id: number
    name: string
    description?: string | null | undefined
    visibility?: string | null | undefined
    audience_type?: string | null | undefined
  }
}

export interface PouleInviteCreateResponse {
  inviteUrl: string
  dashboardUrl?: string | null | undefined
  tokenExpiresAt?: string | null | undefined
  maxUses?: number | null | undefined
}

const pouleCreateCountrySchema = z
  .object({
    id: z.number(),
    name: z.string(),
    image_path: nullableString,
    flag_url: nullableString,
  })
  .passthrough()

const pouleCreateLeagueSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    image_path: nullableString,
  })
  .passthrough()

const pouleCreateTeamSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    logo_url: nullableString,
    is_placeholder: z.boolean().optional(),
  })
  .passthrough()

const pouleCreateTournamentSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    image_path: nullableString,
    seasons: z
      .array(z.object({ id: z.number(), name: z.string() }).passthrough())
      .optional(),
  })
  .passthrough()

const pouleCreateResponseSchema = z
  .object({
    success: z.boolean().optional(),
    poule: z
      .object({
        id: z.number(),
        name: z.string(),
        description: nullableString,
        visibility: nullableString,
        audience_type: nullableString,
      })
      .passthrough(),
  })
  .passthrough()

const pouleInviteCreateResponseSchema = z
  .object({
    invite_url: z.string(),
    dashboard_url: nullableString,
    token_expires_at: nullableString,
    max_uses: nullableNumber,
  })
  .passthrough()

export async function fetchPouleCreateCountries(): Promise<PouleCreateCountry[]> {
  const response = await api.get<unknown>('/countries')
  const raw = toRecord(response)?.countries
  const parsed = z.array(pouleCreateCountrySchema).safeParse(raw)
  if (!parsed.success) throw new Error('Landen konden niet worden geladen.')
  return parsed.data
}

export async function fetchPouleCreateLeagues(countryId: number): Promise<PouleCreateLeague[]> {
  const response = await api.get<unknown>(`/leagues?country_id=${countryId}`)
  const raw = toRecord(response)?.leagues
  const parsed = z.array(pouleCreateLeagueSchema).safeParse(raw)
  if (!parsed.success) throw new Error('Competities konden niet worden geladen.')
  return parsed.data
}

export async function fetchPouleCreateTeams(leagueId: number): Promise<PouleCreateTeam[]> {
  const response = await api.get<unknown>(`/teams?league_id=${leagueId}`)
  const raw = toRecord(response)?.teams
  const parsed = z.array(pouleCreateTeamSchema).safeParse(raw)
  if (!parsed.success) throw new Error('Clubs konden niet worden geladen.')
  return parsed.data
}

export async function fetchPouleCreateTournaments(): Promise<PouleCreateTournament[]> {
  const response = await api.get<unknown>('/tournaments')
  const raw = toRecord(response)?.tournaments
  const parsed = z.array(pouleCreateTournamentSchema).safeParse(raw)
  if (!parsed.success) throw new Error('Toernooien konden niet worden geladen.')
  return parsed.data
}

export type PouleCreatePayload = {
  name: string
  description: string
  visibility: 'public' | 'private'
  requiresApproval: boolean
  audienceType: 'friends' | 'company'
  predictionReleaseMode: 'all_open' | 'stage_by_stage'
  pouleType: 'club' | 'competition' | 'tournament'
  scopeType: 'App\\Models\\Team' | 'App\\Models\\League'
  scopeId: number
  thumbnailImage?: File | undefined
}

export async function createPoule(payload: PouleCreatePayload): Promise<PouleCreateResponse> {
  const formData = new FormData()
  formData.append('name', payload.name.trim())
  formData.append('description', payload.description.trim())
  formData.append('visibility', payload.visibility)
  formData.append('poule_type', payload.pouleType)
  formData.append('scope_type', payload.scopeType)
  formData.append('scope_id', String(payload.scopeId))
  formData.append('requires_approval', payload.requiresApproval ? '1' : '0')
  formData.append('audience_type', payload.audienceType)
  formData.append('prediction_release_mode', payload.predictionReleaseMode)
  if (payload.thumbnailImage) formData.append('thumbnail_image', payload.thumbnailImage)

  const response = await api.post<unknown>('/poules', formData)
  const parsed = pouleCreateResponseSchema.safeParse(response)
  if (!parsed.success) {
    console.error('[poules] Invalid create poule payload', parsed.error.flatten())
    throw new Error('De aangemaakte poule kon niet worden verwerkt.')
  }
  return parsed.data
}

export async function createPouleInvite(pouleId: number): Promise<PouleInviteCreateResponse> {
  const response = await api.post<unknown>(`/poules/${pouleId}/invites`, {})
  const parsed = pouleInviteCreateResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid invite payload', parsed.error.flatten())
    throw new Error('De invite-link kon niet worden gemaakt.')
  }

  return {
    inviteUrl: parsed.data.invite_url,
    dashboardUrl: parsed.data.dashboard_url,
    tokenExpiresAt: parsed.data.token_expires_at,
    maxUses: parsed.data.max_uses,
  }
}

export function useCreatePouleInvite(pouleId: number | null) {
  return useMutation({
    mutationFn: () => createPouleInvite(pouleId as number),
  })
}

async function fetchPouleInfo(pouleId: number): Promise<PouleInfoResponse> {
  const response = await api.get<unknown>(`/poules/${pouleId}/info`)
  const parsed = pouleInfoResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid poule info payload', parsed.error.flatten())
    throw new Error('Poule info kon niet worden geladen.')
  }

  return parsed.data
}

async function fetchPublicPoules(
  query: string,
  page = 1,
): Promise<PublicPouleSearchResponse> {
  const params = new URLSearchParams({ page: String(page) })
  const endpoint = query ? '/poules/search' : '/poules/get-poules'
  if (query) params.set('q', query)

  const response = await api.get<unknown>(`${endpoint}?${params.toString()}`)
  const parsed = rawPublicPouleSearchResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid public poule search payload', parsed.error.flatten())
    throw new Error('Poules konden niet worden gezocht.')
  }

  return parsed.data
}

async function fetchPouleStages(pouleId: number): Promise<Stage[]> {
  const response = await api.get<unknown>(`/poules/${pouleId}/stages`)
  const parsed = stagesEnvelopeSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid stages payload', parsed.error.flatten())
    throw new Error('Poule fases konden niet worden geladen.')
  }

  return Array.isArray(parsed.data) ? parsed.data : parsed.data.stages
}

async function fetchPouleDashboardBootstrap(
  pouleId: number,
): Promise<PouleDashboardBootstrapResponse> {
  const params = new URLSearchParams({
    include:
      'dashboard,round_winner,quiz_stats,quiz_availability,bonus_availability,prediction_availability,advanced_fantasy_availability,badges,activities,rule,closure_summary',
    activities_limit: '20',
    badges_limit: '5',
  })
  const response = await api.get<unknown>(
    `/poules/${pouleId}/dashboard-bootstrap?${params.toString()}`,
  )
  const parsed = rawPouleDashboardBootstrapResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid dashboard payload', parsed.error.flatten())
    throw new Error('Poule dashboard kon niet worden geladen.')
  }

  return {
    ...parsed.data,
    activities:
      parsed.data.activities == null
        ? parsed.data.activities
        : normalizePouleActivityFeed(
            parsed.data.activities,
            parsed.data.meta?.activities_limit ?? 20,
          ),
  }
}

async function fetchPouleAppRounds(pouleId: number): Promise<PouleAppRound[]> {
  const response = await api.get<unknown>(`/poules/${pouleId}/app-rounds`)
  const parsed = rawAppRoundsResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid app rounds payload', parsed.error.flatten())
    throw new Error('Speelrondes konden niet worden geladen.')
  }

  return parsed.data.rounds.sort(compareAppRounds)
}

async function fetchPouleRoundRanking(
  pouleId: number,
  appRoundId: number,
): Promise<PouleRoundRankingEntry[]> {
  const response = await api.get<unknown>(`/poules/${pouleId}/app-rounds/${appRoundId}/ranking`)
  const parsed = rawRoundRankingResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid round ranking payload', parsed.error.flatten())
    throw new Error('Speelrondestand kon niet worden geladen.')
  }

  return parsed.data.ranking.map((entry, index) => {
    const user = entry.poule_user?.user

    return {
      position: entry.position ?? index + 1,
      points: entry.points ?? 0,
      is_me: entry.is_me === true,
      user_id: user?.id ?? undefined,
      user_name: getRoundRankingUserName(user, index),
      profile_image: user?.profile_image ?? undefined,
    }
  })
}

async function fetchPouleActivities(
  pouleId: number,
  limit = 20,
): Promise<PouleActivityFeed> {
  const params = new URLSearchParams({
    limit: String(limit),
  })
  const response = await api.get<unknown>(`/poules/${pouleId}/activities?${params.toString()}`)

  return normalizePouleActivityFeed(response, limit)
}

async function fetchPouleLiveMatches(pouleId: number): Promise<FootballMatch[]> {
  const params = new URLSearchParams({
    page: '1',
    status: 'live',
  })
  const response = await api.get<unknown>(`/poules/${pouleId}/matches?${params.toString()}`)
  const parsed = rawLiveMatchesResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid live matches payload', parsed.error.flatten())
    throw new Error('Live wedstrijden konden niet worden geladen.')
  }

  return (parsed.data.data ?? parsed.data.matches ?? parsed.data.items ?? []).map(toFootballMatch)
}

async function fetchPouleMatchesByStatus(
  pouleId: number,
  page: number,
  status: MatchListStatus,
): Promise<PaginatedMatchesResponse> {
  const params = new URLSearchParams({
    page: String(page),
    status,
  })
  const response = await api.get<unknown>(`/poules/${pouleId}/matches?${params.toString()}`)
  const parsed = rawPaginatedMatchesSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid paginated matches payload', parsed.error.flatten())
    throw new Error('Wedstrijden konden niet worden geladen.')
  }

  return {
    data: parsed.data.data.map(toFootballMatch),
    currentPage: parsed.data.meta?.current_page ?? parsed.data.current_page ?? page,
    lastPage: parsed.data.meta?.last_page ?? parsed.data.last_page ?? page,
    total: parsed.data.meta?.total ?? parsed.data.total ?? parsed.data.data.length,
    predictionStageContext: parsed.data.prediction_stage_context
      ? {
          mode: parsed.data.prediction_stage_context.mode,
          active_stage_name: parsed.data.prediction_stage_context.active_stage_name,
          stage_lock_starts_at_utc: parsed.data.prediction_stage_context.stage_lock_starts_at_utc,
          stage_lock_starts_at_nl: parsed.data.prediction_stage_context.stage_lock_starts_at_nl,
          seconds_until_stage_lock: parsed.data.prediction_stage_context.seconds_until_stage_lock,
          predictions_open: parsed.data.prediction_stage_context.predictions_open,
        }
      : null,
  }
}

async function fetchPouleAllBadges(
  pouleId: number,
  options?: { compact?: boolean },
): Promise<AllBadgesResponse> {
  const params = new URLSearchParams()
  if (options?.compact) params.set('compact', 'true')

  const response = await api.get<unknown>(
    `/poules/${pouleId}/me/all-badges${params.size > 0 ? `?${params.toString()}` : ''}`,
  )
  return parseAllBadgesResponse(response)
}

export async function getUserBadges(
  pouleId: number,
  userId: number,
  options: GetUserBadgesOptions = {},
): Promise<AllBadgesResponse> {
  const lang = options.lang ?? getLocale()
  const params = new URLSearchParams({
    lang,
  })

  if (options.compact != null) {
    params.set('compact', options.compact ? 'true' : 'false')
  }

  const response = await api.get<unknown>(
    `/poules/${pouleId}/users/${userId}/all-badges?${params.toString()}`,
    {
      headers: {
        'X-Locale': lang,
      },
    },
  )

  return parseAllBadgesResponse(response)
}

async function fetchPouleMatchDetail(
  matchId: number,
  pouleMatchId?: number | null,
): Promise<FootballMatch> {
  const params = new URLSearchParams()
  if (typeof pouleMatchId === 'number') {
    params.set('poule_match_id', String(pouleMatchId))
  }

  const response = await api.get<unknown>(
    `/poules/matches/${matchId}${params.size > 0 ? `?${params.toString()}` : ''}`,
  )

  return parseMatchDetailResponse(response)
}

async function fetchGenericMatchDetail(matchId: number): Promise<FootballMatch> {
  // Use the same accessible poule-match contract as the Flutter app. The
  // generic /matches endpoint does not include the poule context required for
  // predictions, live events and the final score and can fail for valid users.
  const response = await api.get<unknown>(`/poules/matches/${matchId}`)
  return parseMatchDetailResponse(response)
}

async function fetchOtherPredictions(
  pouleId: number,
  pouleMatchId: number,
): Promise<OtherPredictionsResponse> {
  const response = await api.get<unknown>(
    `/poules/${pouleId}/matches/${pouleMatchId}/other-predictions`,
  )
  const parsed = rawOtherPredictionsResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid other predictions payload', parsed.error.flatten())
    throw new Error('Andere voorspellingen konden niet worden geladen.')
  }

  return parsed.data
}

async function fetchPredictionStats(
  pouleId: number,
  pouleMatchId: number,
): Promise<MatchPredictionStatsResponse> {
  const response = await api.get<unknown>(
    `/poules/${pouleId}/matches/${pouleMatchId}/prediction-stats`,
  )
  const parsed = rawPredictionStatsResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid prediction stats payload', parsed.error.flatten())
    throw new Error('Prediction stats konden niet worden geladen.')
  }

  return {
    match: {
      id: parsed.data.match.id,
      status: parsed.data.match.status,
      kickoff_utc: parsed.data.match.kickoff_utc ?? undefined,
      home_team: toTeam(parsed.data.match.home_team, 'Thuis'),
      away_team: toTeam(parsed.data.match.away_team, 'Uit'),
    },
    summary: parsed.data.summary,
    most_used_prediction: parsed.data.most_used_prediction ?? undefined,
    outcome_distribution: parsed.data.outcome_distribution
      ? {
          total: parsed.data.outcome_distribution.total,
          home_team: toTeam(parsed.data.outcome_distribution.home_team, 'Thuis'),
          away_team: toTeam(parsed.data.outcome_distribution.away_team, 'Uit'),
          items: parsed.data.outcome_distribution.items,
        }
      : undefined,
    top_scorelines: parsed.data.top_scorelines,
  }
}

async function requestMatchChatAccess(
  matchId: number,
  pouleMatchId: number,
): Promise<void> {
  await api.post<unknown>(`/poules/matches/${matchId}/chat-access`, {
    poule_match_id: pouleMatchId,
  })
}

async function savePoulePrediction(
  input: SavePoulePredictionInput,
): Promise<SavedPoulePrediction> {
  const response = await api.post<unknown>(`/matches/${input.matchId}/predictions`, {
    poule_id: input.pouleId,
    home_pred: input.homePred,
    away_pred: input.awayPred,
    home_mvp: null,
    away_mvp: null,
  })
  const parsed = savedPredictionSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules] Invalid prediction save payload', parsed.error.flatten())
    throw new Error('Voorspelling kon niet worden opgeslagen.')
  }

  return parsed.data
}

export function usePouleInfo(pouleId: number | null) {
  return useQuery({
    queryKey: ['poule-info', pouleId],
    queryFn: () => fetchPouleInfo(pouleId as number),
    enabled: pouleId != null,
    staleTime: 30_000,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  })
}

export function useSearchPublicPoules(query: string, enabled = true) {
  const normalizedQuery = query.trim()

  return useQuery({
    queryKey: ['public-poule-search', normalizedQuery],
    queryFn: () => fetchPublicPoules(normalizedQuery),
    enabled: enabled && normalizedQuery.length >= 2,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function usePublicPoules(query: string, enabled = true) {
  const normalizedQuery = query.trim()

  return useInfiniteQuery({
    queryKey: ['public-poules', normalizedQuery],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchPublicPoules(normalizedQuery, pageParam),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.current_page ?? 1
      const lastPageNumber = lastPage.last_page ?? currentPage
      return currentPage < lastPageNumber ? currentPage + 1 : undefined
    },
    enabled:
      enabled &&
      (normalizedQuery.length === 0 || normalizedQuery.length >= 2),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function usePouleStages(pouleId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['poule-stages', pouleId],
    queryFn: () => fetchPouleStages(pouleId as number),
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
  })
}

export function usePouleDashboardBootstrap(pouleId: number | null, enabled = true) {
  const locale = getLocale()

  return useQuery({
    queryKey: ['poule-dashboard-bootstrap', pouleId, locale],
    queryFn: () => fetchPouleDashboardBootstrap(pouleId as number),
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function usePouleAppRounds(pouleId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['poule-app-rounds', pouleId],
    queryFn: () => fetchPouleAppRounds(pouleId as number),
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function usePouleRoundRanking(
  pouleId: number | null,
  appRoundId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['poule-round-ranking', pouleId, appRoundId],
    queryFn: () => fetchPouleRoundRanking(pouleId as number, appRoundId as number),
    enabled: pouleId != null && appRoundId != null && enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function usePouleActivities(
  pouleId: number | null,
  options?: {
    enabled?: boolean
    limit?: number
    placeholderData?: PouleActivityFeed | null | undefined
  },
) {
  const limit = options?.limit ?? 20
  const locale = getLocale()

  return useQuery({
    queryKey: ['poule-activities', pouleId, limit, locale],
    queryFn: () => fetchPouleActivities(pouleId as number, limit),
    enabled: pouleId != null && (options?.enabled ?? true),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData ?? options?.placeholderData ?? undefined,
  })
}

export function usePouleLiveMatches(pouleId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['poule-live-matches', pouleId],
    queryFn: () => fetchPouleLiveMatches(pouleId as number),
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function useInfinitePouleMatches(
  pouleId: number | null,
  status: MatchListStatus,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ['poule-matches', pouleId, status],
    queryFn: ({ pageParam }) =>
      fetchPouleMatchesByStatus(pouleId as number, Number(pageParam), status),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.lastPage ? lastPage.currentPage + 1 : undefined,
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
  })
}

export function usePouleAllBadges(
  pouleId: number | null,
  options?: { compact?: boolean; enabled?: boolean },
) {
  return useQuery({
    queryKey: ['poule-all-badges', pouleId, options?.compact === true],
    queryFn: () =>
      fetchPouleAllBadges(
        pouleId as number,
        options?.compact === true ? { compact: true } : undefined,
      ),
    enabled: pouleId != null && (options?.enabled ?? true),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function usePouleUserBadges(
  pouleId: number | null,
  userId: number | null,
  options?: GetUserBadgesOptions & { enabled?: boolean },
) {
  const lang = options?.lang ?? getLocale()

  return useQuery({
    queryKey: ['poule-user-badges', pouleId, userId, options?.compact === true, lang],
    queryFn: () =>
      getUserBadges(pouleId as number, userId as number, {
        lang,
        ...(options?.compact != null ? { compact: options.compact } : {}),
      }),
    enabled: pouleId != null && userId != null && (options?.enabled ?? true),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function useMatchDetail(
  matchId: number | null,
  options?: {
    pouleMatchId?: number | null
    enabled?: boolean
    useGenericFallback?: boolean
  },
) {
  return useQuery({
    queryKey: ['match-detail', matchId, options?.pouleMatchId ?? null, options?.useGenericFallback === true],
    queryFn: () =>
      options?.useGenericFallback === true
        ? fetchGenericMatchDetail(matchId as number)
        : fetchPouleMatchDetail(matchId as number, options?.pouleMatchId),
    enabled: matchId != null && (options?.enabled ?? true),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function useOtherPredictions(
  pouleId: number | null,
  pouleMatchId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['other-predictions', pouleId, pouleMatchId],
    queryFn: () => fetchOtherPredictions(pouleId as number, pouleMatchId as number),
    enabled: pouleId != null && pouleMatchId != null && enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function usePredictionStats(
  pouleId: number | null,
  pouleMatchId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['prediction-stats', pouleId, pouleMatchId],
    queryFn: () => fetchPredictionStats(pouleId as number, pouleMatchId as number),
    enabled: pouleId != null && pouleMatchId != null && enabled,
    staleTime: 30_000,
    retry: (count, error) => !(error instanceof ApiError && error.status === 403) && count < 2,
    placeholderData: (previousData) => previousData,
  })
}

export function useMatchChatAccess() {
  return useMutation({
    mutationFn: ({ matchId, pouleMatchId }: { matchId: number; pouleMatchId: number }) =>
      requestMatchChatAccess(matchId, pouleMatchId),
  })
}

export function useSavePoulePrediction(pouleId: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: savePoulePrediction,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-matches', pouleId, 'future'] }),
        queryClient.invalidateQueries({ queryKey: ['poule-dashboard-bootstrap', pouleId] }),
      ])
    },
  })
}

async function joinPoule(pouleId: number): Promise<{
  membership?: { status?: MembershipStatus; role?: MembershipRole } | null
}> {
  const response = await api.post<unknown>(`/poules/${pouleId}/join`, {})
  const parsed = z
    .object({
      membership: z
        .object({
          status: z.string().optional(),
          role: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .passthrough()
    .safeParse(response)

  if (!parsed.success) {
    throw new Error('Deelnemen aan de poule is niet gelukt.')
  }

  return {
    membership: parsed.data.membership
      ? {
          status: isMembershipStatus(parsed.data.membership.status)
            ? parsed.data.membership.status
            : null,
          role: isMembershipRole(parsed.data.membership.role)
            ? parsed.data.membership.role
            : null,
        }
      : null,
  }
}

export function useJoinPoule(pouleId: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => joinPoule(pouleId as number),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-info', pouleId] }),
        queryClient.invalidateQueries({ queryKey: ['joined-poules'] }),
        queryClient.invalidateQueries({ queryKey: ['public-poules'] }),
        queryClient.invalidateQueries({ queryKey: ['public-poule-search'] }),
      ])
    },
  })
}

function isMembershipStatus(value: string | undefined): value is Exclude<MembershipStatus, null> {
  return value === 'accepted' || value === 'pending' || value === 'rejected'
}

function isMembershipRole(value: string | null | undefined): value is Exclude<MembershipRole, null> {
  return value === 'owner' || value === 'admin' || value === 'member'
}

export function getMembershipStatus(
  membership?: PouleInfoResponse['membership'],
): MembershipStatus {
  return membership?.status ?? null
}

export function inferPouleUxStageType(stages: Stage[] | undefined): PouleUxStageType {
  const stage = [...(stages ?? [])]
    .sort((left, right) => {
      const leftOrder = left.order ?? Number.MAX_SAFE_INTEGER
      const rightOrder = right.order ?? Number.MAX_SAFE_INTEGER
      if (left.is_current && !right.is_current) return -1
      if (!left.is_current && right.is_current) return 1
      return leftOrder - rightOrder
    })[0]

  if (!stage) return 'classic_league'
  if (stage.type === 'group') return 'group'
  if (stage.type === 'knockout') return 'knockout'
  if (stage.name.toLowerCase().includes('league')) return 'league_stage'
  return 'classic_league'
}

function parseMatchDetailResponse(response: unknown): FootballMatch {
  const direct = rawFootballMatchSchema.safeParse(response)
  if (direct.success) return toFootballMatch(direct.data)

  const wrappedSchema = z
    .object({
      data: rawFootballMatchSchema.nullable().optional(),
      match: rawFootballMatchSchema.nullable().optional(),
    })
    .passthrough()

  const wrapped = wrappedSchema.safeParse(response)
  if (wrapped.success) {
    const payload = wrapped.data.data ?? wrapped.data.match
    if (payload) return toFootballMatch(payload)
  }

  console.error('[poules] Invalid match detail payload', {
    direct: direct.success ? null : direct.error.flatten(),
    wrapped: wrapped.success ? null : wrapped.error.flatten(),
  })
  throw new Error('Wedstrijddetail kon niet worden geladen.')
}

function toFootballMatch(raw: z.infer<typeof rawFootballMatchSchema>): FootballMatch {
  return {
    id: raw.id,
    season_id: raw.season_id ?? undefined,
    league_id: raw.league_id ?? undefined,
    status: normalizeMatchStatus(raw.status),
    predict_status: normalizePredictStatus(raw.predict_status),
    is_predictable: raw.is_predictable === true,
    prediction_locked_by_stage_release: raw.prediction_locked_by_stage_release ?? undefined,
    prediction_locked_reason: raw.prediction_locked_reason ?? undefined,
    prediction_lock_context: raw.prediction_lock_context
      ? {
          current_stage_name: raw.prediction_lock_context.current_stage_name ?? undefined,
          match_stage_name: raw.prediction_lock_context.match_stage_name ?? undefined,
        }
      : undefined,
    round: typeof raw.round === 'number' ? raw.round : undefined,
    joker: raw.joker === true,
    joker_multiplier: raw.joker_multiplier ?? undefined,
    kickoff_utc: normalizeRequiredString(raw.kickoff_utc),
    kickoff_nl: raw.kickoff_nl ?? undefined,
    kickoff_time_tbd: raw.kickoff_time_tbd === true,
    minute: raw.minute ?? undefined,
    extra_minute: raw.extra_minute ?? undefined,
    home_team: toTeam(raw.home_team, 'Thuis'),
    away_team: toTeam(raw.away_team, 'Uit'),
    score: raw.score
      ? {
          home: raw.score.home ?? undefined,
          away: raw.score.away ?? undefined,
        }
      : undefined,
    home_form: raw.home_form ?? [],
    away_form: raw.away_form ?? [],
    stage_id: raw.stage_id ?? undefined,
    stage_name: raw.stage_name ?? undefined,
    stage_type: raw.stage_type ?? undefined,
    external_group_id: raw.external_group_id ?? undefined,
    group_id: raw.group_id ?? undefined,
    poule: raw.poule
      ? {
          id: raw.poule.id ?? undefined,
          name: raw.poule.name ?? undefined,
          poule_type: raw.poule.poule_type ?? undefined,
          scope_type: raw.poule.scope_type ?? undefined,
        }
      : undefined,
    poule_match_id: raw.poule_match_id ?? undefined,
    chat_active: raw.chat_active === true,
    chat_writable: raw.chat_writable === true,
    user_prediction: raw.user_prediction
      ? {
          home_pred: raw.user_prediction.home_pred ?? undefined,
          away_pred: raw.user_prediction.away_pred ?? undefined,
          points_awarded: raw.user_prediction.points_awarded ?? undefined,
          virtual_points: raw.user_prediction.virtual_points ?? undefined,
          poule_id: raw.user_prediction.poule_id ?? undefined,
        }
      : undefined,
    events: raw.events.map(toMatchEvent),
    homeLineup: raw.home_team?.lineup.map(toLineupPlayer) ?? [],
    homeBench: raw.home_team?.bench.map(toLineupPlayer) ?? [],
    awayLineup: raw.away_team?.lineup.map(toLineupPlayer) ?? [],
    awayBench: raw.away_team?.bench.map(toLineupPlayer) ?? [],
  }
}

function toTeam(
  raw: z.infer<typeof rawTeamSchema> | undefined,
  fallbackName: string,
): Team {
  return {
    id: raw?.id ?? 0,
    name:
      normalizeOptionalString(raw?.display_name) ??
      normalizeOptionalString(raw?.name) ??
      normalizeOptionalString(raw?.short_name) ??
      fallbackName,
    short_name: raw?.short_name ?? undefined,
    logo_url: normalizeOptionalString(raw?.logo_url) ?? '',
    is_placeholder: raw?.is_placeholder === true,
  }
}

function toLineupPlayer(raw: z.infer<typeof rawLineupPlayerSchema>): LineupPlayer {
  return {
    id: raw.id ?? undefined,
    name:
      normalizeOptionalString(raw.name) ??
      normalizeOptionalString(raw.player_name) ??
      normalizeOptionalString(raw.display_name) ??
      'Speler',
    position: raw.position ?? undefined,
    jersey: raw.jersey ?? raw.shirt_number ?? raw.squad_number ?? undefined,
    is_starting: raw.is_starting === true,
    image:
      normalizeOptionalString(raw.image) ??
      normalizeOptionalString(raw.photo_url) ??
      normalizeOptionalString(raw.image_url) ??
      undefined,
  }
}

function toMatchEvent(raw: z.infer<typeof rawMatchEventSchema>): MatchEvent {
  return {
    id: raw.id,
    minute: raw.minute ?? 0,
    extra: raw.extra ?? undefined,
    team_side: raw.team_side ?? undefined,
    player_id: raw.player_id ?? undefined,
    player: raw.player ?? undefined,
    info: raw.info ?? undefined,
    type: raw.type ?? undefined,
    team_badge: raw.team_badge ?? undefined,
    addition: raw.addition ?? undefined,
  }
}

function normalizeMatchStatus(value?: string | null): MatchStatus {
  const normalized = value?.trim().toLowerCase()

  if (normalized === 'live') return 'live'
  if (normalized === 'ht' || normalized === 'half_time' || normalized === 'halftime') return 'ht'
  if (normalized === 'ft' || normalized === 'finished' || normalized === 'full_time') return 'ft'
  if (normalized === 'cancelled' || normalized === 'canceled') return 'cancelled'
  if (normalized === 'postponed') return 'postponed'
  return 'scheduled'
}

function normalizePredictStatus(value?: string | null): PredictStatus {
  const normalized = value?.trim().toLowerCase()
  if (normalized === 'open') return 'open'
  if (normalized === 'mixed') return 'mixed'
  return 'closed'
}

function toBadgeOverviewItem(
  raw: z.infer<typeof rawBadgeOverviewItemSchema>,
): BadgeOverviewItem {
  return {
    id: raw.id,
    badge: toBadgeCatalogItem(raw.badge),
    tier: raw.tier ? toBadgeTier(raw.tier) : undefined,
    earned: raw.earned === true,
    earned_count: raw.earned_count ?? 0,
    last_earned_at: raw.last_earned_at ?? undefined,
    club: raw.club ? toBadgeClub(raw.club) : undefined,
    earned_instances: raw.earned_instances.map(toBadgeUser),
    earned_by_users_count: raw.earned_by_users_count ?? raw.earned_by_users.length,
    earned_by_users: raw.earned_by_users.map(toBadgeUserSummary),
  }
}

function toBadgeCatalogItem(
  raw: z.infer<typeof rawBadgeCatalogItemSchema>,
): BadgeCatalogItem {
  return {
    id: raw.id ?? undefined,
    key: raw.key ?? undefined,
    name: raw.name,
    icon_url: raw.icon_url ?? undefined,
    description: raw.description ?? undefined,
    category: raw.category ?? undefined,
    points_reward: raw.points_reward ?? undefined,
    difficulty: raw.difficulty ?? undefined,
  }
}

function toBadgeTier(raw: z.infer<typeof rawBadgeTierSchema>): BadgeTier {
  return {
    key: raw.key ?? undefined,
    label: raw.label ?? undefined,
  }
}

function toBadgeClub(raw: z.infer<typeof rawBadgeClubSchema>): BadgeClub {
  return {
    id: raw.id ?? undefined,
    name: raw.name ?? undefined,
    logo: raw.logo ?? undefined,
  }
}

function toBadgeUser(raw: z.infer<typeof rawBadgeUserSchema>): UserBadgeInstance {
  return {
    id: raw.id,
    earned_at: normalizeRequiredString(raw.earned_at),
    reason: raw.reason ?? undefined,
    meta: raw.meta ?? undefined,
    badge: raw.badge ? toPartialBadgeCatalogItem(raw.badge) : undefined,
    tier: raw.tier ? toBadgeTier(raw.tier) : undefined,
    club: raw.club ? toBadgeClub(raw.club) : undefined,
    poule_id: raw.poule_id ?? undefined,
    poule: raw.poule
      ? {
          id: raw.poule.id ?? undefined,
          name: raw.poule.name ?? undefined,
          thumbnail_url: raw.poule.thumbnail_url ?? undefined,
          competition: raw.poule.competition
            ? {
                logo_url: raw.poule.competition.logo_url ?? undefined,
              }
            : undefined,
        }
      : undefined,
    prediction: raw.prediction
      ? {
          home_pred: raw.prediction.home_pred ?? undefined,
          away_pred: raw.prediction.away_pred ?? undefined,
          points_awarded: raw.prediction.points_awarded ?? undefined,
          virtual_points: raw.prediction.virtual_points ?? undefined,
          poule_id: raw.prediction.poule_id ?? undefined,
        }
      : undefined,
    match: raw.match ? toFootballMatch(raw.match) : undefined,
    matches: raw.matches.map(toFootballMatch),
  }
}

function toBadgeUserSummary(
  raw: z.infer<typeof rawBadgeUserSummarySchema>,
): BadgeUserSummary {
  return {
    id: raw.id,
    name: raw.name,
    display_name: raw.display_name ?? undefined,
    profile_image: raw.profile_image ?? undefined,
  }
}

function toPartialBadgeCatalogItem(
  raw: NonNullable<z.infer<typeof rawBadgeUserSchema>['badge']>,
): Partial<BadgeCatalogItem> {
  const badge: Partial<BadgeCatalogItem> = {}

  if (typeof raw.id === 'number') badge.id = raw.id
  if (typeof raw.key === 'string' && raw.key.trim()) badge.key = raw.key
  if (typeof raw.name === 'string' && raw.name.trim()) badge.name = raw.name
  if (typeof raw.icon_url === 'string' && raw.icon_url.trim()) badge.icon_url = raw.icon_url
  if (typeof raw.description === 'string' && raw.description.trim()) {
    badge.description = raw.description
  }
  if (typeof raw.category === 'string' && raw.category.trim()) badge.category = raw.category
  if (typeof raw.points_reward === 'number') badge.points_reward = raw.points_reward
  if (typeof raw.difficulty === 'number') badge.difficulty = raw.difficulty

  return badge
}

function parseAllBadgesResponse(response: unknown): AllBadgesResponse {
  const parsed = rawAllBadgesResponseSchema.safeParse(response)

  if (parsed.success) {
    return {
      data: parsed.data.data.map(toBadgeOverviewItem),
    }
  }

  return normalizeAllBadgesResponse(response)
}

function normalizeAllBadgesResponse(response: unknown): AllBadgesResponse {
  const items = getUnknownArray(
    response,
    ['data', 'items', 'badges', 'all_badges'],
  ) ?? (Array.isArray(response) ? response : [])

  return {
    data: items.map((item, index) => normalizeBadgeOverviewItem(item, index)),
  }
}

function normalizeBadgeOverviewItem(
  value: unknown,
  index: number,
): BadgeOverviewItem {
  const record = toRecord(value) ?? {}
  const badgeRecord = toRecord(record.badge) ?? record
  const earnedInstances = getUnknownArray(record, [
    'earned_instances',
    'instances',
    'user_badges',
    'badges',
  ]).map((item, itemIndex) => normalizeLooseBadgeUser(item, itemIndex))
  const earnedByUsers = getUnknownArray(record, [
    'earned_by_users',
    'users',
    'recent_users',
  ]).map((item, itemIndex) => normalizeLooseBadgeUserSummary(item, itemIndex))

  const lastEarnedAt =
    readString(record, ['last_earned_at', 'earned_at', 'lastEarnedAt']) ??
    earnedInstances
      .slice()
      .sort((left, right) => getLooseTimeValue(right.earned_at) - getLooseTimeValue(left.earned_at))[0]
      ?.earned_at

  const earnedCount =
    readNumber(record, ['earned_count', 'count', 'earnedCount']) ??
    earnedInstances.length

  return {
    id:
      readNumber(record, ['id', 'badge_id']) ??
      readNumber(badgeRecord, ['id']) ??
      index + 1,
    badge: {
      id: readNumber(badgeRecord, ['id']) ?? undefined,
      key: readString(badgeRecord, ['key', 'badge_key']) ?? undefined,
      name:
        readString(badgeRecord, ['name', 'title', 'label']) ??
        readString(record, ['name', 'title', 'label']) ??
        'Badge',
      icon_url:
        readString(badgeRecord, ['icon_url', 'icon']) ??
        readString(record, ['icon_url', 'icon']) ??
        undefined,
      description:
        readString(badgeRecord, ['description', 'reason']) ??
        readString(record, ['description', 'reason']) ??
        undefined,
      category:
        readString(badgeRecord, ['category']) ??
        readString(record, ['category']) ??
        undefined,
      points_reward:
        readNumber(badgeRecord, ['points_reward']) ??
        readNumber(record, ['points_reward']) ??
        undefined,
      difficulty:
        readNumber(badgeRecord, ['difficulty']) ??
        readNumber(record, ['difficulty']) ??
        undefined,
    },
    tier: normalizeLooseTier(record.tier ?? badgeRecord.tier ?? record),
    earned:
      readBoolean(record, ['earned']) ??
      (earnedCount > 0 || earnedInstances.length > 0),
    earned_count: earnedCount,
    last_earned_at: lastEarnedAt ?? undefined,
    club: normalizeLooseClub(record.club ?? badgeRecord.club ?? record.team),
    earned_instances: earnedInstances,
    earned_by_users_count:
      readNumber(record, ['earned_by_users_count', 'users_count']) ??
      earnedByUsers.length,
    earned_by_users: earnedByUsers,
  }
}

function normalizeLooseBadgeUser(value: unknown, index: number): UserBadgeInstance {
  const record = toRecord(value) ?? {}
  const badgeRecord = toRecord(record.badge)
  const predictionRecord = toRecord(record.prediction ?? record.user_prediction)
  const pouleRecord = toRecord(record.poule)

  return {
    id: readNumber(record, ['id', 'badge_user_id']) ?? index + 1,
    earned_at: readString(record, ['earned_at', 'created_at', 'updated_at']) ?? '',
    reason: readString(record, ['reason', 'description']) ?? undefined,
    meta: toRecord(record.meta) ?? undefined,
    badge: badgeRecord ? toPartialLooseBadgeCatalogItem(badgeRecord) : undefined,
    tier: normalizeLooseTier(record.tier ?? badgeRecord?.tier),
    club: normalizeLooseClub(record.club ?? record.team),
    poule_id: readNumber(record, ['poule_id']) ?? undefined,
    poule: pouleRecord
      ? {
          id: readNumber(pouleRecord, ['id']) ?? undefined,
          name: readString(pouleRecord, ['name']) ?? undefined,
          thumbnail_url: readString(pouleRecord, ['thumbnail_url', 'profile_url']) ?? undefined,
          competition: toRecord(pouleRecord.competition)
            ? {
                logo_url:
                  readString(toRecord(pouleRecord.competition), ['logo_url']) ?? undefined,
              }
            : undefined,
        }
      : undefined,
    prediction: predictionRecord
      ? {
          home_pred: readNumber(predictionRecord, ['home_pred']) ?? undefined,
          away_pred: readNumber(predictionRecord, ['away_pred']) ?? undefined,
          points_awarded: readNumber(predictionRecord, ['points_awarded']) ?? undefined,
          virtual_points: readNumber(predictionRecord, ['virtual_points']) ?? undefined,
          poule_id: readNumber(predictionRecord, ['poule_id']) ?? undefined,
        }
      : undefined,
    match: toLooseFootballMatch(record.match),
    matches: getUnknownArray(record, ['matches']).map((item) =>
      normalizeLooseFootballMatch(toRecord(item) ?? {}),
    ),
  }
}

function normalizeLooseBadgeUserSummary(
  value: unknown,
  index: number,
): BadgeUserSummary {
  const record = toRecord(value)

  return {
    id: readNumber(record, ['id', 'user_id']) ?? index + 1,
    name:
      readString(record, ['name', 'display_name', 'username']) ??
      `Speler ${index + 1}`,
    display_name: readString(record, ['display_name']) ?? undefined,
    profile_image:
      readString(record, ['profile_image', 'profile_image_url', 'avatar']) ?? undefined,
  }
}

function toPartialLooseBadgeCatalogItem(
  record: Record<string, unknown>,
): Partial<BadgeCatalogItem> {
  const badge: Partial<BadgeCatalogItem> = {}

  const id = readNumber(record, ['id'])
  const key = readString(record, ['key', 'badge_key'])
  const name = readString(record, ['name', 'title', 'label'])
  const icon = readString(record, ['icon_url', 'icon'])
  const description = readString(record, ['description', 'reason'])
  const category = readString(record, ['category'])
  const pointsReward = readNumber(record, ['points_reward'])
  const difficulty = readNumber(record, ['difficulty'])

  if (typeof id === 'number') badge.id = id
  if (key) badge.key = key
  if (name) badge.name = name
  if (icon) badge.icon_url = icon
  if (description) badge.description = description
  if (category) badge.category = category
  if (typeof pointsReward === 'number') badge.points_reward = pointsReward
  if (typeof difficulty === 'number') badge.difficulty = difficulty

  return badge
}

function normalizeLooseTier(value: unknown): BadgeTier | undefined {
  const record = toRecord(value)
  if (!record) return undefined

  const key = readString(record, ['key', 'payloadTierKey', 'tier_key'])
  const label = readString(record, ['label', 'payloadTierLabel', 'tier_label'])

  if (!key && !label) return undefined

  return {
    key: key ?? undefined,
    label: label ?? undefined,
  }
}

function normalizeLooseClub(value: unknown): BadgeClub | undefined {
  const record = toRecord(value)
  if (!record) return undefined

  const id = readNumber(record, ['id', 'team_id'])
  const name = readString(record, ['name', 'display_name', 'team_name', 'team_display_name'])
  const logo = readString(record, ['logo', 'logo_url', 'team_logo'])

  if (id == null && !name && !logo) return undefined

  return {
    id: id ?? undefined,
    name: name ?? undefined,
    logo: logo ?? undefined,
  }
}

function toLooseFootballMatch(value: unknown) {
  const record = toRecord(value)
  return record ? normalizeLooseFootballMatch(record) : undefined
}

function normalizePouleActivityFeed(
  response: unknown,
  fallbackLimit = 20,
): PouleActivityFeed {
  if (Array.isArray(response)) {
    return {
      items: response
        .map((item) => toRecord(item))
        .filter((item): item is Record<string, unknown> => item !== undefined),
      limit: fallbackLimit,
    }
  }

  const record = toRecord(response)
  if (record) {
    const directItems = getUnknownArray(response, ['data', 'items'])
    const directLimit =
      readNumber(record, ['limit']) ??
      readNumber(toRecord(record.meta), ['limit']) ??
      fallbackLimit

    if (
      'items' in record ||
      'data' in record ||
      'limit' in record ||
      'meta' in record
    ) {
      return {
        items: directItems
          .map((item) => toRecord(item))
          .filter((item): item is Record<string, unknown> => item !== undefined),
        limit: directLimit,
      }
    }
  }

  const direct = rawPouleActivityFeedSchema.safeParse(response)
  if (direct.success) return direct.data

  const candidates = [record?.data, record?.activities]

  for (const candidate of candidates) {
    if (candidate !== undefined) {
      return normalizePouleActivityFeed(candidate, fallbackLimit)
    }
  }

  console.error('[poules] Invalid activities payload', response)
  throw new Error('Activiteit kon niet worden geladen.')
}

function normalizeLooseFootballMatch(record: Record<string, unknown>): FootballMatch {
  const homeTeam = normalizeLooseTeam(toRecord(record.home_team), 'Thuis')
  const awayTeam = normalizeLooseTeam(toRecord(record.away_team), 'Uit')
  const predictionRecord = toRecord(record.user_prediction)
  const scoreRecord = toRecord(record.score)

  return {
    id: readNumber(record, ['id']) ?? 0,
    status: normalizeMatchStatus(readString(record, ['status'])),
    predict_status: normalizePredictStatus(readString(record, ['predict_status'])),
    is_predictable: readBoolean(record, ['is_predictable']) ?? false,
    kickoff_utc: readString(record, ['kickoff_utc', 'kickoff_nl']) ?? '',
    kickoff_nl: readString(record, ['kickoff_nl']) ?? undefined,
    minute: readNumber(record, ['minute']) ?? undefined,
    extra_minute: readNumber(record, ['extra_minute']) ?? undefined,
    home_team: homeTeam,
    away_team: awayTeam,
    score: scoreRecord
      ? {
          home: readNumber(scoreRecord, ['home']) ?? undefined,
          away: readNumber(scoreRecord, ['away']) ?? undefined,
        }
      : undefined,
    stage_name: readString(record, ['stage_name', 'round']) ?? undefined,
    user_prediction: predictionRecord
      ? {
          home_pred: readNumber(predictionRecord, ['home_pred']) ?? undefined,
          away_pred: readNumber(predictionRecord, ['away_pred']) ?? undefined,
          points_awarded: readNumber(predictionRecord, ['points_awarded']) ?? undefined,
          virtual_points: readNumber(predictionRecord, ['virtual_points']) ?? undefined,
          poule_id: readNumber(predictionRecord, ['poule_id']) ?? undefined,
        }
      : undefined,
  }
}

function normalizeLooseTeam(
  record: Record<string, unknown> | undefined,
  fallbackName: string,
) {
  return {
    id: readNumber(record, ['id']) ?? 0,
    name:
      readString(record, ['display_name', 'short_name', 'name']) ??
      fallbackName,
    short_name: readString(record, ['short_name']) ?? undefined,
    logo_url: readString(record, ['logo_url', 'logo']) ?? '',
    is_placeholder: readBoolean(record, ['is_placeholder']) ?? false,
  }
}

function toRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function compareAppRounds(a: PouleAppRound, b: PouleAppRound) {
  if (typeof a.sequence === 'number' && typeof b.sequence === 'number') {
    return a.sequence - b.sequence
  }

  if (typeof a.sequence === 'number') return -1
  if (typeof b.sequence === 'number') return 1
  return a.id - b.id
}

function getRoundRankingUserName(
  user: z.infer<typeof rawRoundRankingUserSchema> | null | undefined,
  index: number,
) {
  const displayName = user?.display_name?.trim()
  if (displayName) return displayName

  const fallbackName = [user?.first_name, user?.last_name]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .trim()

  if (fallbackName) return fallbackName
  return `Speler ${index + 1}`
}

function getUnknownArray(
  value: unknown,
  keys: string[],
): unknown[] {
  const record = toRecord(value)
  if (!record) return []

  for (const key of keys) {
    if (Array.isArray(record[key])) return record[key] as unknown[]
  }

  return []
}

function readString(
  value: Record<string, unknown> | undefined,
  keys: string[],
) {
  if (!value) return undefined

  for (const key of keys) {
    const candidate = value[key]
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }

  return undefined
}

function readNumber(
  value: Record<string, unknown> | undefined,
  keys: string[],
) {
  if (!value) return undefined

  for (const key of keys) {
    const candidate = value[key]
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate
    if (typeof candidate === 'string' && candidate.trim()) {
      const parsed = Number(candidate)
      if (Number.isFinite(parsed)) return parsed
    }
  }

  return undefined
}

function readBoolean(
  value: Record<string, unknown> | undefined,
  keys: string[],
) {
  if (!value) return undefined

  for (const key of keys) {
    const candidate = value[key]
    if (typeof candidate === 'boolean') return candidate
  }

  return undefined
}

function getLooseTimeValue(value?: string | null) {
  if (!value) return 0
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

function normalizeRequiredString(value?: string | null) {
  return value?.trim() ?? ''
}

function normalizeOptionalString(value?: string | null) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}
